import { useEffect, useState } from "react";
import { tempoRelativo } from "../formatos";
import { IconeSino } from "../icones";
import { LinkInterno } from "../navegacao";
import { Carregando } from "./Basicos";
import { Botao } from "./Botao";
import { CampoTextoLongo } from "./Campo";
import { Flutuante } from "./Flutuante";

export type CategoriaDaEquipe = "PENDENCIA" | "ALERTA" | "AVISO";
export type ItemDaEquipe = {
  id: string;
  categoria: CategoriaDaEquipe;
  titulo: string;
  mensagem: string;
  criada_em: string;
  link?: string | null;
  /** Chegou depois da ultima vez que ESTE administrador abriu o sino (qualquer categoria, desde 06/10). */
  novo?: boolean;
  /** O catalogo deixa o operador resolver (o que tem acao no sistema sai sozinho quando a acao e feita). */
  pode_resolver: boolean;
};
/** `novas`: o numero do sino, o que e novo pra este administrador. Sem ele (backend antigo), o numero e o trabalho aberto. */
export type ContagemDaEquipe = { pendencias: number; alertas: number; avisos_novos: number; novas?: number };
type Aba = "ALERTA" | "PENDENCIA" | "AVISO";

const VAZIO: Record<Aba, string> = {
  ALERTA: "Nenhum alerta. Tudo funcionando.",
  PENDENCIA: "Nenhuma pendência. Tudo em dia.",
  AVISO: "Nenhum aviso ainda.",
};

/**
 * Sino da equipe (spec 010): tres abas. Alertas (urgente, no topo), Pendencias (trabalho: some quando e feito) e Avisos
 * (informativo). O numero do sino e o que e NOVO pra quem olha (Vitor, 06/10): abrir o sino zera o numero, e a
 * pendencia continua na lista ate alguem resolver. As abas mostram o total aberto, que e a carga de trabalho.
 * Resolver na mao abre, no proprio item, o campo do que foi feito. So apresentacao: quem busca e resolve e o app.
 */
export function SinoDaEquipe({ contagem, alertas, pendencias, avisos, carregando, lado = "direita", verTodasHref, aoAbrir, aoAbrirItem, aoResolver, aoVerAvisos }: {
  contagem: ContagemDaEquipe;
  alertas: ItemDaEquipe[];
  pendencias: ItemDaEquipe[];
  avisos: ItemDaEquipe[];
  carregando?: boolean;
  lado?: "direita" | "esquerda";
  verTodasHref?: string;
  aoAbrir?: () => void;
  aoAbrirItem: (n: ItemDaEquipe) => void;
  aoResolver: (n: ItemDaEquipe, observacao: string) => Promise<boolean> | boolean;
  /** A aba de avisos foi aberta: o app marca o "visto ate" deste operador. */
  aoVerAvisos?: () => void;
}) {
  const [aberto, setAberto] = useState(false);
  const [aba, setAba] = useState<Aba>("PENDENCIA");
  const trabalho = contagem.pendencias + contagem.alertas;
  const novas = contagem.novas ?? trabalho;
  // Abriu: o numero some na hora, sem esperar o servidor confirmar o "visto". Volta quando chegar mais do que havia.
  const [novasAoAbrir, setNovasAoAbrir] = useState<number | null>(null);
  useEffect(() => {
    if (novasAoAbrir !== null && novas < novasAoAbrir) setNovasAoAbrir(null);
  }, [novas, novasAoAbrir]);
  const numero = novasAoAbrir !== null && novas <= novasAoAbrir ? 0 : novas;
  const escolher = (a: Aba) => {
    setAba(a);
    if (a === "AVISO" && contagem.avisos_novos > 0) aoVerAvisos?.();
  };
  const alternar = () => {
    const abrir = !aberto;
    setAberto(abrir);
    if (abrir) {
      // Abre onde esta o mais urgente.
      escolher(contagem.alertas > 0 ? "ALERTA" : contagem.pendencias > 0 ? "PENDENCIA" : contagem.avisos_novos > 0 ? "AVISO" : "PENDENCIA");
      if (contagem.novas !== undefined) setNovasAoAbrir(novas);
      aoAbrir?.();
    }
  };
  const rotulo = numero ? `Notificações, ${numero} ${numero === 1 ? "nova" : "novas"}` : "Notificações";
  const itens = aba === "ALERTA" ? alertas : aba === "PENDENCIA" ? pendencias : avisos;
  const abas: { valor: Aba; rotulo: string; contagem?: number; ponto?: boolean }[] = [
    { valor: "ALERTA", rotulo: "Alertas", contagem: contagem.alertas },
    { valor: "PENDENCIA", rotulo: "Pendências", contagem: contagem.pendencias },
    { valor: "AVISO", rotulo: "Avisos", ponto: contagem.avisos_novos > 0 },
  ];
  return (
    <div className="mtc-sino">
      <button type="button" className="mtc-sino__botao" aria-label={rotulo} aria-expanded={aberto} aria-haspopup="dialog" onClick={alternar}>
        <IconeSino tamanho={18} />
        {numero > 0 ? (
          <span className={`mtc-sino__contagem${contagem.alertas ? " mtc-sino__contagem--alerta" : ""}`} aria-hidden>{numero > 99 ? "99+" : numero}</span>
        ) : null}
      </button>
      {/* Abre pelo Flutuante, igual a casca da area (spec 038, 1.3): o veu pega o toque de fora, entao fechar nao clica
          no que esta embaixo; no celular e a folha inferior com fundo e Fechar. */}
      <Flutuante aberto={aberto} aoFechar={() => setAberto(false)} titulo="Notificações" posicao={lado === "esquerda" ? "abaixo-esquerda" : "abaixo-direita"} classe="mtc-sino__flutuante mtc-sino-equipe">
        <div className="mtc-sino-equipe__abas" role="tablist" aria-label="Tipo de notificação">
          {abas.map((a) => (
            <button key={a.valor} type="button" role="tab" aria-selected={aba === a.valor} className={`mtc-sino-equipe__aba${a.valor === "ALERTA" && contagem.alertas ? " mtc-sino-equipe__aba--alerta" : ""}`} onClick={() => escolher(a.valor)}>
              {a.rotulo}
              {a.contagem ? <span className="mtc-sino-equipe__numero">{a.contagem}</span> : null}
              {a.ponto ? <span className="mtc-sino-equipe__ponto" aria-label="com avisos novos" /> : null}
            </button>
          ))}
        </div>
        {carregando && !itens.length ? (
          <Carregando compacto texto="Buscando" />
        ) : !itens.length ? (
          <p className="mtc-sino__vazio">{VAZIO[aba]}</p>
        ) : (
          <ul className="mtc-sino__lista" role="tabpanel" aria-label={abas.find((a) => a.valor === aba)!.rotulo}>
            {itens.map((n) => (
              <ItemDoSino key={n.id} n={n} aoAbrir={() => { setAberto(false); aoAbrirItem(n); }} aoResolver={aoResolver} />
            ))}
          </ul>
        )}
        {verTodasHref && (
          <div className="mtc-sino-equipe__rodape">
            <LinkInterno href={verTodasHref} onClick={() => setAberto(false)}>Ver todas as notificações</LinkInterno>
          </div>
        )}
      </Flutuante>
    </div>
  );
}

function ItemDoSino({ n, aoAbrir, aoResolver }: { n: ItemDaEquipe; aoAbrir: () => void; aoResolver: (n: ItemDaEquipe, observacao: string) => Promise<boolean> | boolean }) {
  const [resolvendo, setResolvendo] = useState(false);
  const [observacao, setObservacao] = useState("");
  const [enviando, setEnviando] = useState(false);
  const confirmar = async () => {
    setEnviando(true);
    try {
      if (await aoResolver(n, observacao.trim())) setResolvendo(false);
    } finally {
      setEnviando(false);
    }
  };
  return (
    <li className="mtc-sino__item mtc-sino-equipe__item" data-categoria={n.categoria} data-lida={n.novo === undefined ? n.categoria === "AVISO" : !n.novo}>
      <button type="button" onClick={aoAbrir}>
        <span className="mtc-sino__ponto" aria-hidden />
        <span className="mtc-sino__textos">
          <span className="mtc-sino__titulo">{n.titulo}</span>
          <span className="mtc-sino__mensagem">{n.mensagem}</span>
          <span className="mtc-sino__quando">{tempoRelativo(n.criada_em)}{n.novo ? ". Novo" : ""}</span>
        </span>
      </button>
      {n.pode_resolver && !resolvendo && (
        <div className="mtc-sino-equipe__acoes">
          <Botao variante="fantasma" tamanho="pequeno" onClick={() => setResolvendo(true)}>Resolver</Botao>
        </div>
      )}
      {resolvendo && (
        <div className="mtc-sino-equipe__resolver">
          <CampoTextoLongo rotulo="O que foi feito (opcional)" valor={observacao} aoMudar={setObservacao} linhas={2} maxLength={500} placeholder="Ex.: devolvido por PIX em 15/09." />
          <div className="mtc-sino-equipe__botoes">
            <Botao variante="fantasma" tamanho="pequeno" onClick={() => setResolvendo(false)} disabled={enviando}>Cancelar</Botao>
            <Botao tamanho="pequeno" carregando={enviando} onClick={() => void confirmar()}>Marcar como resolvida</Botao>
          </div>
        </div>
      )}
    </li>
  );
}
